import React from 'react';
import { FormattedMessage } from 'react-intl';
import { defineMessages } from 'react-intl.macro';

import type { FormatMessage } from '@tmapy/intl';
import { WaitingAlert } from '@tmapy/style-guide';

import { orderPriceMessages as pm } from './orderMessages';

const msg = defineMessages({
  loading: {
    id: 'newOrder.price.loading',
    defaultMessage: 'Probíhá výpočet předběžné ceny…',
  },
  note: {
    id: 'newOrder.price.note',
    defaultMessage: 'Jedná se o orientační kalkulaci.',
  },
});

export type PriceSummaryRow = {
  key: string | number;
  name: string;
  area_ha?: string | null;
  price_list_item_name?: string | null;
  vat_rate?: string | null;
  price_gross?: string | null;
  /** Vlastní popisek řádku; nahrazuje výchozí „plocha × položka ceníku“. */
  note?: React.ReactNode;
};

/** Poplatek je mimo DPH (sazba 0 %), cena s DPH se proto rovná základu. */
export const createHandlingFeeRows = (
  formatMessage: FormatMessage,
  handlingFeeNet?: string | null,
): PriceSummaryRow[] =>
  handlingFeeNet
    ? [
        {
          key: 'handling_fee',
          name: formatMessage(pm.handlingFeeRow),
          vat_rate: '0',
          price_gross: handlingFeeNet,
        },
      ]
    : [];

type PriceSummaryProps = {
  rows: PriceSummaryRow[];
  /** Vlastní nadpis sekce; bez něj se použije výdejový podle `isPreview`. */
  title?: React.ReactNode;
  total_gross?: string | null;
  total_net?: string | null;
  total_vat?: string | null;
  isLoading?: boolean;
  isPreview?: boolean;
};

export const PriceSummary: React.FC<PriceSummaryProps> = ({
  rows,
  title,
  total_gross,
  total_net,
  total_vat,
  isLoading,
  isPreview,
}) => {
  if (!isLoading && !rows.length && !total_gross) return null;

  return (
    <section>
      <h3 className='sg-u-type-h2 sg-a-ml-2 sg-a-mb-1'>
        {title ?? <FormattedMessage {...(isPreview ? pm.previewTitle : pm.detailTitle)} />}
      </h3>
      <div className='sg-u-box sg-a-p-2'>
        {isLoading ? (
          <WaitingAlert>
            <FormattedMessage {...msg.loading} />
          </WaitingAlert>
        ) : (
          <div className='sg-u-vs-1'>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '4px 16px', alignItems: 'center' }}>
              {rows.map((row) => (
                <React.Fragment key={row.key}>
                  <span>{row.name}</span>
                  <span className='sg-a-ta-r sg-a-ws-nw sg-u-c-body-darkest sg-u-type-xsm'>
                    {row.note ??
                      (row.area_ha && row.price_list_item_name && (
                        <FormattedMessage
                          {...pm.publishsetRow}
                          values={{
                            area: Number(row.area_ha),
                            priceListItemName: row.price_list_item_name,
                          }}
                        />
                      ))}
                    {row.vat_rate && (
                      <span className='sg-a-d-b'>
                        <FormattedMessage
                          {...pm.publishsetRowVat}
                          values={{ vatRate: Number(row.vat_rate) }}
                        />
                      </span>
                    )}
                  </span>
                  <span className='sg-a-ta-r sg-a-ws-nw'>
                    {row.price_gross && (
                      <FormattedMessage {...pm.amount} values={{ price: row.price_gross }} />
                    )}
                  </span>
                </React.Fragment>
              ))}
            </div>

            {total_gross && (
              <div className='sg-a-pt-1' style={{ borderTop: '1px solid hsl(0, 0%, 85%)' }}>
                <div
                  className='sg-a-ml-a'
                  style={{ display: 'grid', gridTemplateColumns: 'auto auto', gap: '2px 16px' }}
                >
                  {total_net && (
                    <>
                      <span className='sg-u-c-body-darkest'>
                        <FormattedMessage {...pm.totalNetLabel} />
                      </span>
                      <span className='sg-a-ta-r sg-u-c-body-darkest'>
                        <FormattedMessage {...pm.amount} values={{ price: total_net }} />
                      </span>
                    </>
                  )}
                  {total_vat && (
                    <>
                      <span className='sg-u-c-body-darkest'>
                        <FormattedMessage {...pm.totalVatLabel} />
                      </span>
                      <span className='sg-a-ta-r sg-u-c-body-darkest'>
                        <FormattedMessage {...pm.amount} values={{ price: total_vat }} />
                      </span>
                    </>
                  )}
                  <div style={{ gridColumn: '1 / -1', borderTop: '1px solid hsl(0, 0%, 85%)' }} />
                  <span className='sg-u-type-bold'>
                    <FormattedMessage {...pm.totalGrossLabel} />
                  </span>
                  <span className='sg-a-ta-r sg-u-type-bold'>
                    <FormattedMessage {...pm.amount} values={{ price: total_gross }} />
                  </span>
                </div>
              </div>
            )}

            {isPreview && (
              <div className='sg-u-type-sml sg-u-type-italic sg-u-c-body-darkest'>
                <FormattedMessage {...msg.note} />
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};
